import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import CustomTh from '../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../components/table/CustomTd';
import TableFunction from '../../../../../../../components/table/TableFunction';
import CustomChip from '../../../../../../../components/CustomChip';
import { colors } from '../../../../../../../enums/colors';
import {
    EMPTY_VALUES, errorMsg, getDateFormat,
} from '../../../../../../../Apis/functions';
import {DEVICE_ACCESS_TYPE_MODEL} from '../../../../../Devices/Model/Model';
import HandleChange from '../../../../../../../Helpers/HandleChange';
import {
    ATTENDANCE_COLOR_MODEL,
    NO_DEPARTMENT,
} from '../../../Model/Model';
import { GetAttendanceColor } from '../../../../../../../reduxToolkit/AttendanceConfig/AttendanceConfigApi';
import {getDepartments} from "../../../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {getSubmissionSearchType} from "../../History/Model/Model";
import {
    DEFAULT_LEFT,
    DEFAULT_PAGINATION_START,
    SEARCH_MODEL
} from "../../../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../../../reduxToolkit/Search/SearchApi";
import {
    GetAttendancePeriodReview, SaveAttendanceAdjustment
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {
    hasAdjustedChanges,
    hasInvalidAdjustedRows,
    ROW_ATTENDANCE_ADJUSTED,
    SUBMISSION_PERIOD_REVIEW_FILTER_MODEL
} from "../Model/Model";
import routes from "../../../../../../../Routes/routes";
import {useLocation, useNavigate} from "react-router-dom";
import CustomDateTimePicker from "../../../../../../../components/CustomDateTimePicker";
import CommonPeriodReviewTableApp from "./CommonPeriodReviewTableApp";
import {toast} from "react-toastify";
import {GetAttendanceDepartmentAssigned} from "../../../../../../../reduxToolkit/EmployeePayRoll/EmployeePayRollApi";
import {IconPlusFill, PersonOutlinedChip} from "../../../../../../../components/buttons/IconButtons";
import ListDisplayWithTooltip from "../../../../../../../components/ListDisplayWithTooltip";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import {PLACEMENT_MODEL} from "../../../../../../../Helpers/PositionModel";
import {permissionObj} from "../../../../../../../Helpers/permission";
import {ErrorCode} from "../../../../../../../Apis/ErrorCode";
/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : FullStack Developer
 * @Email : kpozos@ibl.mx
 *
 */
const PeriodReviewTableApp = ({
    searchElement,
    setBodyForExport,
    option,
    modified,
    setModified,
    registerAction,
    activeStep,
    subView
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const {getAttendancePeriodReview} = useSelector((state) => state.AttendanceAdjustmentSlice);
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const tableId = "tbl_prt_submission_period_review"; // Set the table ID for identification
    const searchId = 'txt_prt_submission_period_review';
    const [results, setResults] = useState({ content: [], totalElements: 0 }); // Store the search results
    const leftPosition = DEFAULT_LEFT;
    // Filters selected before opening the review confirmation, so coming back keeps them.
    const restoredFilters = location?.state?.periodReviewFilters;
    const [item , setItem] = useState(() => ({...SUBMISSION_PERIOD_REVIEW_FILTER_MODEL, ...restoredFilters}));
    const [color, setColor] = useState({ ...ATTENDANCE_COLOR_MODEL.setDefaultData() });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [departments, setDepartments] = useState([]);
    const initFilters = useRef(false);
    const [rowsAdjusted, setRowsAdjusted] = useState(null);
    const isAdmin = DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_READ);
    // Without the manage permission the period can only be read, so every picker
    // stays disabled. Department supervisors manage their own attendances.
    const canManageAttendances = DEV || isAttendanceSupervisor ||
        permission?.includes(permissionObj?.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_MANAGE_ATTENDANCES);
    // The scheduled shift column is fixed next to the name one, so it starts where
    // the name column ends. That width is only known once the header is rendered.
    const nameHeadRef = useRef(null);
    const [scheduledLeft, setScheduledLeft] = useState(DEFAULT_LEFT);

    /**
     * Saves a draft adjustment for a specific attendance row.
     *
     * Behavior:
     * - Transforms the row into API format
     * - Dispatches persistence action
     * - On success:
     *    - Shows success notification
     *    - Updates local `rowsAdjusted` state
     * - On failure:
     *    - Displays error message
     *
     * @param {Object} row - Adjusted row data
     * @param {string} adjustmentType - Type of adjustment (ENTRY / EXIT)
     */
    const saveDraft = (row, adjustmentType) =>{
        const item = ROW_ATTENDANCE_ADJUSTED.toRegister(row, adjustmentType);
        dispatch(SaveAttendanceAdjustment(item)).then(({ payload: { data: { data, success, code } } }) => {
            if (success){
                toast.success(t("has_been_save_draft_successfully"));
                setRowsAdjusted(prev => ({
                    ...prev,
                    [row.attendanceId]: row
                }));
            }else {
                // The allowed range comes from the response: the backend widens the
                // scheduled shift with its tolerance windows, so the row bounds would
                // name a narrower range than the one it accepts.
                errorMsg(code, {
                    from: getDateFormat(data?.from),
                    to: getDateFormat(data?.to)
                });
                const isEntry = adjustmentType === DEVICE_ACCESS_TYPE_MODEL.ENTRY.name;

                // The scheduled shift rules run in the backend, so a date it rejects for
                // that reason stays in the picker and the field is flagged with the
                // reason, the same way an out of order pair is.
                if (code === ErrorCode.ATTENDANCE_ADJUSTMENT_OUT_OF_SHIFT) {
                    setRowsAdjusted(prev => ({
                        ...prev,
                        [row.attendanceId]: {
                            ...row,
                            ...(isEntry
                                ? {
                                    entryFullAdjustedIsValid: false,
                                    entryInvalidText: 'outside_scheduled_shift_short'
                                }
                                : {
                                    exitFullAdjustedIsValid: false,
                                    exitInvalidText: 'outside_scheduled_shift_short'
                                })
                        }
                    }));
                    return;
                }

                // The adjustment was rejected, so the row keeps the value it held
                // before the edit. The picker holds the rejected value internally and
                // a same-valued prop does not clear it, so its reset version is bumped
                // to remount it with the restored value.
                setRowsAdjusted(prev => {
                    const previous = prev?.[row.attendanceId] || {};

                    return {
                        ...prev,
                        [row.attendanceId]: {
                            ...previous,
                            entryResetVersion: (previous.entryResetVersion || 0) + (isEntry ? 1 : 0),
                            exitResetVersion: (previous.exitResetVersion || 0) + (isEntry ? 0 : 1),
                        }
                    };
                });
            }
        });
    }

    const {
        entryFullOnChange,
        exitFullOnChange,
        getTotalTime,
        getGeneralStatus,
        getEntryExitColumn,
        getScheduledColumn
    } = CommonPeriodReviewTableApp({
        setRowsAdjusted,
        color,
        saveDraft,
        // Every pick is saved through group/create, so the shift rules are answered there.
        validateOutOfShift: false
    });

    /**
     * Initializes data on component mount.
     *
     * Flow:
     * 1. Fetches attendance period review
     * 2. Loads departments (based on role)
     * 3. Fetches attendance color configuration
     *
     * Runs once on mount.
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodReview()).then(({ payload: { data: { data, success } } }) => {
            if (success && data){
                loadDepartments();

                dispatch(GetAttendanceColor()).then(
                    ({
                         payload: {
                             data: { data },
                         },
                     }) => {
                        setColor({ ...ATTENDANCE_COLOR_MODEL, ...data });
                    },
                );
            }
        });
    }, []);

    /**
     * Triggers data reload when department filter changes.
     *
     * Behavior:
     * - Skips first render using ref flag
     * - Fetches data from first page with current search filter
     */
    useEffect(() => {
        if(!initFilters.current){
            initFilters.current = true;
            return;
        }
        getData(DEFAULT_PAGINATION_START, { searchElement});
    }, [item?.departmentId]);

    /**
     * Drops the restored filters from the route state once they are applied.
     *
     * Only that key is removed: the tab and sub-view keys still drive the view on
     * re-renders.
     */
    useEffect(() => {
        if (restoredFilters == null) return;
        const { periodReviewFilters, ...rest } = location.state;
        navigate(location.pathname, { replace: true, state: rest });
    }, []);

    /**
     * Loads departments available for filtering.
     *
     * Behavior:
     * - If user is supervisor:
     *    - Loads only assigned departments
     *    - Includes "no department" if null values exist
     * - If admin:
     *    - Loads all departments
     *    - Prepends "no department" option
     *
     * Updates local `departments` state.
     */
    const loadDepartments = () => {
        const noDepartment = {
            id: NO_DEPARTMENT,
            name: t('no_department')
        };

        const action = isAttendanceSupervisor
            ? GetAttendanceDepartmentAssigned()
            : getDepartments();

        dispatch(action).then(({ payload: { data: { data = [], success } = {} } = {} }) => {
            if (!success) return;

            let departments;

            if (isAttendanceSupervisor) {
                const hasNull = data.some(e => e.id === null);
                departments = data.filter(e => e.id !== null);

                if (hasNull) {
                    departments = [noDepartment, ...departments];
                }
            } else {
                departments = [noDepartment, ...data];
            }

            setDepartments(departments);
        });
    };

    /**
     * Generates table header configuration.
     *
     * Behavior:
     * - Defines columns for attendance review
     * - Conditionally includes supervisor column for admins
     *
     * @returns {Array<JSX.Element>} Table header cells
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head' headRef={nameHeadRef}>
                {t('name')}
            </CustomTh>,
            <CustomTh key={`th_prt_scheduled_${scheduledLeft}`} tableId={tableId} fixedXY={true} left={scheduledLeft}>
                {t('scheduled_shift')}
            </CustomTh>,
            isAdmin && <CustomTh tableId={tableId}>{t('supervisor')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_captured')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('new_entry_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_captured')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('new_exit_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('total_time')}</CustomTh>,
        ];
    };

    /**
     * Generates table body rows.
     *
     * Behavior:
     * - Maps API results into table rows
     * - Merges adjusted row state (`rowsAdjusted`)
     * - Renders:
     *    - Employee info and department
     *    - Supervisors (admin only)
     *    - Entry/exit captured values
     *    - Editable datetime pickers
     *    - Status indicators
     *    - Total time calculation
     *
     * @returns {Object}
     * @returns {Array<JSX.Element>} returns.body - Rendered rows
     * @returns {Array<Object>} returns.data - Raw data
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0)
            return {
                body: [],
                data: [],
            };

        let body = results?.content.map((item, index) => {
            const current = rowsAdjusted?.[item.attendanceId] || {};
            const disabled = !canManageAttendances || current?.disabled || false;

            const entryFullAdjusted = current?.entryFullAdjusted || null;
            const exitFullAdjusted = current?.exitFullAdjusted || null;

            const entryStatusAdjusted = current?.entryStatusAdjusted || null;
            const exitStatusAdjusted = current?.exitStatusAdjusted || null;
            const supervisors = item?.supervisors ? JSON.parse(item.supervisors) : [];

            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={'first-column py-3'}>
                        <div className={'mb-1'}>
                            <CustomTooltip
                                text={item?.fullName}
                                classes={'text-ellipsis-1-row'}
                                showIfTruncated={true}
                                placement={PLACEMENT_MODEL.TOP}
                            />
                        </div>

                        { item?.departmentName &&
                            <CustomChip
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                legend={item?.departmentName }
                                variant={'compact'}
                            />
                        }
                    </CustomTd>

                    <CustomTd key={`td_prt_scheduled_${item?.attendanceId}_${scheduledLeft}`} tableId={tableId} fixed={true} left={scheduledLeft} classes={'py-3'}>
                        { getScheduledColumn(item.shiftStart, item.shiftEnd) }
                    </CustomTd>

                    { isAdmin &&
                        <CustomTd tableId={tableId} classes={"py-3"}>
                            <ListDisplayWithTooltip
                                items={supervisors}
                                renderItem={(u) => u.fullName}
                                maxInline={2}
                                emptyValue={EMPTY_VALUES.DEFAULT}
                                icon={<IconPlusFill width={20} height={20} />}
                                itemIcon={<PersonOutlinedChip className="icon-color-general-950" />}
                            />
                        </CustomTd>
                    }

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getEntryExitColumn(item.entryDeviceName, item.entryDateOriginal, item.entryAccessMethodId, item.attendanceEntryStatusId)}
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <div className={"w-272px"}>
                            <CustomDateTimePicker
                                key={`dp_prt_entry_date_adjusted_${item?.attendanceId}_${current?.entryResetVersion || 0}`}
                                id={`dp_prt_entry_date_adjusted_${item?.attendanceId}_${index}`}
                                name={'entryFullAdjusted'}
                                value={entryFullAdjusted}
                                disabled={disabled}
                                onAccept={(e) => entryFullOnChange(e, current, item)}
                                noMarginBottom={true}
                                onlySelecting={true}
                                isValid={current.entryFullAdjustedIsValid}
                                isValidText={current.entryInvalidText || "date_invalid"}
                                minutesStep={1}
                                secondsStep={1}
                            />
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getGeneralStatus(item.attendanceEntryStatusId, entryStatusAdjusted, item?.entryDateAdjustedBy, disabled) }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getEntryExitColumn(item.exitDeviceName, item.exitDateOriginal, item.exitAccessMethodId, item.attendanceExitStatusId)}
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <div className={"w-272px"}>
                            <CustomDateTimePicker
                                key={`dp_prt_exit_date_adjusted_${item?.attendanceId}_${current?.exitResetVersion || 0}`}
                                id={`dp_prt_exit_date_adjusted_${item?.attendanceId}_${index}`}
                                name={'exitFullAdjusted'}
                                value={exitFullAdjusted}
                                disabled={disabled}
                                onAccept={(e) => exitFullOnChange(e, current, item)}
                                noMarginBottom={true}
                                onlySelecting={true}
                                isValid={current.exitFullAdjustedIsValid}
                                isValidText={current.exitInvalidText || "date_invalid"}
                                minutesStep={1}
                                secondsStep={1}
                            />
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        { getGeneralStatus(item.attendanceExitStatusId, exitStatusAdjusted, item?.exitDateAdjustedBy, disabled) }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes={"py-3"}>
                        {getTotalTime(item, current)}
                    </CustomTd>
                </>
            );
        });

        return {
            body: body,
            data: results?.content,
        };
    };

    /**
     * Fetches paginated attendance data.
     *
     * Behavior:
     * - Builds search payload based on filters and department
     * - Stores payload for export usage
     * - Executes search request
     * - Maps response into:
     *    - `results` (table data)
     *    - `rowsAdjusted` (local editable state)
     *
     * @param {number} page - Page index
     * @param {Object} params - Search params
     * @param {string} params.searchElement - Search query
     */
    const getData = (page, { searchElement }) => {
        let attendanceSearchType = getSubmissionSearchType(item?.departmentId);

        const body = SEARCH_MODEL.setObject({
            page,
            size:rowsPerPage,
            model:option,
            key:searchElement,
            attendanceSearchType,
            modelId: item?.departmentId === NO_DEPARTMENT ? null: item?.departmentId
        })
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    const initial = {};

                    data.content.forEach(item => {
                        initial[item.attendanceId] = ROW_ATTENDANCE_ADJUSTED.toSet(item);
                    });

                    setResults(data);
                    setRowsAdjusted(initial);
                }
            },
        );
    };

    const { t, thead, tbody, pagination, rowsPerPage } = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        data: results,
        labelRowsPerPage: 'employees_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement,

        getData,//New
        modified,
        setModified,
        dependencies: [rowsAdjusted, color, scheduledLeft]
    });

    useEffect(() => {
       showButtonActions();
    }, [rowsAdjusted]);

    /**
     * Keeps the scheduled shift column pinned right after the name one.
     *
     * The name column has no fixed width, so its rendered width is measured and
     * used as the left offset of the scheduled column.
     */
    useEffect(() => {
        // The header cell fills the ref on its own effect, so the width is read
        // after paint. Without a measure both columns would stick at 0 and the
        // scheduled one would cover the name.
        const updateScheduledLeft = () => {
            const width = nameHeadRef.current?.offsetWidth;
            if (width) setScheduledLeft(width);
        };

        const frame = requestAnimationFrame(updateScheduledLeft);
        window.addEventListener('resize', updateScheduledLeft);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateScheduledLeft);
        };
    }, [thead, results]);

    /**
     * Determines whether review actions should be available.
     *
     * Behavior:
     * - Checks if any row is in DRAFT state
     * - If true:
     *    - Registers `handleReviewUpdates` action
     * - Otherwise:
     *    - Removes action
     *
     * Updates parent action controls via `registerAction`.
     */
    const showButtonActions = () => {
        if (!rowsAdjusted || Object.keys(rowsAdjusted).length === 0) return;

        // An invalid picker must not be sent for approval, and a row that keeps its
        // captured value has nothing to review, so the action stays off in both cases.
        const showReviewUpdatesBtn = canManageAttendances &&
            !hasInvalidAdjustedRows(rowsAdjusted) &&
            hasAdjustedChanges(rowsAdjusted);

        registerAction(subView, {
            handleReviewUpdates: showReviewUpdatesBtn ? handleReviewUpdates : undefined
        });
    };

    /**
     * Navigates to confirmation screen for reviewed adjustments.
     *
     * Passes:
     * - Current tab (activeStep)
     * - Current subview
     * - Selected department filter
     */
    const handleReviewUpdates= () => {
        navigate(routes.submission.confirmRequest, {
            state: {
                currentTab: activeStep,
                currentSubView: subView,
                periodReviewFilters: { departmentId: item?.departmentId }
            },
        });
    };

    return {
        setResults,
        tableId,
        thead,
        tbody,
        pagination,
        searchId,
        rowsPerPage,
        handleChange,
        item,
        departments,
        getAttendancePeriodReview,
        getData
    };
};

export default PeriodReviewTableApp;
